





import { defineComponent, onMounted, reactive, ref } from '@vue/composition-api';
import axios from 'axios';

const DEFAULT_CHART_WIDTH = 400;

type StockItem = {
	dax: {
		chartUrl: string;
		description: string;
		href: string;
		lastPrice: string;
		netchangePercent: string;
		netchangeValue: string;
		performanceArrow: string;
		quoteDate: string;
		quoteTime: string;
	};
	search: {
		hidden: string;
		href: string;
	};
	index: {
		href: string;
	};
};

export default defineComponent({
	props: {
		/**
		 * Endpoint to load stock data from
		 */
		url: String,
	},
	setup({ url }) {
		const stockTeaser = ref();
		const state = reactive({
			stock: null as StockItem | null,
			isLoading: true,
			hasError: false,
			currentChartWidth: DEFAULT_CHART_WIDTH,
			hiddenFields: '',
		});

		/**
		 * Calculate the client width of the image within the stock teaser.
		 * We have to go via `querySelector` because we can't hand over `ref` in the default slot.
		 * @returns Either the actual width of the image of the default one
		 */
		function getChartWidth() {
			const chartImg = stockTeaser.value?.querySelector('.olb-stock-img');
			return chartImg ? chartImg.clientWidth : DEFAULT_CHART_WIDTH;
		}

		/**
		 * Set the chart URL based on width and height of the chart to optimize its scaling.
		 */
		function calcChartProps() {
			const width = getChartWidth();
			// update current chart width (indication for re-fetching on window resize)
			state.currentChartWidth = width;
			const height = Math.round(width / 2);
			if (state.stock) {
				state.stock.dax.chartUrl += '&width=' + width + '&height=' + height + '&subProperty=0';
			}
		}

		/**
		 * Append hidden field input form params to search field
		 * by splitting up the corresponding asynchronously loaded search href
		 * and inserting them as `v-html` value.
		 */
		function handleFormParams() {
			state.hiddenFields = '';
			const urlParts = state.stock?.search.href.split('?');
			if (urlParts?.length && urlParts.length > 1) {
				const params = urlParts[1].split('&');

				params.forEach((param) => {
					const nameValue = param.split('=');
					const newHiddenInput = document.createElement('input');
					newHiddenInput.setAttribute('type', 'hidden');
					newHiddenInput.setAttribute('name', nameValue[0]);
					newHiddenInput.setAttribute('value', nameValue[1]);
					state.hiddenFields += newHiddenInput.outerHTML;
				});
			}
		}

		/**
		 * Fetch stock data from given ajax endpoint.
		 */
		function getStockData() {
			if (url) {
				axios
					.get(url)
					.then((response) => {
						state.stock = response.data;
						calcChartProps();
						handleFormParams();
						state.isLoading = false;
						state.hasError = false;
					})
					.catch((error) => {
						state.hasError = true;
						console.error('[OlbStock] Endpoint Error', error);
					});
			}
		}

		/**
		 * Re-scale the chart image and re-fetch the stock data
		 * everytime the window is rescaled in the x-direction.
		 * (Is throttled to only reload every n pixels based on the chart width.)
		 */
		function resizeHandler() {
			const chartWidth = getChartWidth();
			if (chartWidth + 50 < state.currentChartWidth || chartWidth - 20 > state.currentChartWidth) {
				getStockData();
			}
		}

		function registerResizeHandler() {
			window.addEventListener('resize', resizeHandler);
		}

		onMounted(() => {
			getStockData();
			registerResizeHandler();
		});

		return { state, stockTeaser };
	},
});
